/**
 * Automation & Campaign Engine — Supabase Data Access Layer
 *
 * All functions operate on snake_case columns to match the DB schema.
 * camelCase transformation happens in the consuming React hooks.
 */

import { supabase, getSupabase } from '../../utils/supabase';
import { remapCampaignNodes } from '../../utils/campaignNodeRemap';
import { canUserSeeRecord, fetchAccessibleOwnerIds } from '../../utils/recordVisibility';

// ---------------------------------------------------------------------------
// Transform helpers
// ---------------------------------------------------------------------------

export function transformTemplateToCamelCase(row) {
  if (!row) return null;
  return {
    id: row.id || row.template_id,
    name: row.name,
    description: row.description,
    triggerType: row.trigger_type,
    triggerConfig: row.trigger_config,
    version: row.version,
    isActive: row.is_active,
    category: row.category,
    isPlaybook: row.is_playbook,
    enrollmentRules: row.enrollment_rules,
    exitConditions: row.exit_conditions,
    createdBy: row.created_by,
    ownershipType: row.ownership_type,
    isVisible: row.is_visible,
    executionScope: row.execution_scope,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    // Stats view fields (optional — present when fetching from crm_campaign_template_stats)
    totalSubscribers: row.total_subscribers,
    activeSubscribers: row.active_subscribers,
    pendingQueueItems: row.pending_queue_items,
    completedQueueItems: row.completed_queue_items,
    failedQueueItems: row.failed_queue_items,
    fundedDeals: row.funded_deals,
    fundedVolume: row.funded_volume,
    // Joined nodes (optional)
    nodes: row.nodes
      ? row.nodes.map(transformNodeToCamelCase)
      : undefined,
  };
}

export function transformNodeToCamelCase(row) {
  if (!row) return null;
  return {
    id: row.id,
    templateId: row.template_id,
    userCampaignId: row.user_campaign_id,
    templateNodeId: row.template_node_id,
    nodeType: row.node_type,
    subType: row.sub_type,
    config: row.config,
    nextNodeId: row.next_node_id,
    falseNodeId: row.false_node_id,
    orderIndex: row.order_index,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function transformUserCampaignToCamelCase(row) {
  if (!row) return null;
  return {
    id: row.id,
    userId: row.user_id,
    templateId: row.template_id,
    templateVersion: row.template_version,
    isEnabled: row.is_enabled,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    // Joined template info (optional -- handles both default FK key and aliased key)
    template: row.crm_campaign_templates
      ? transformTemplateToCamelCase(row.crm_campaign_templates)
      : row.template
        ? transformTemplateToCamelCase(row.template)
        : undefined,
    // Joined nodes (optional)
    nodes: row.crm_user_campaign_nodes
      ? row.crm_user_campaign_nodes.map(transformNodeToCamelCase)
      : undefined,
  };
}

export function transformQueueItemToCamelCase(row) {
  if (!row) return null;
  return {
    id: row.id,
    userCampaignId: row.user_campaign_id,
    contactId: row.contact_id,
    currentNodeId: row.current_node_id,
    status: row.status,
    nodeType: row.node_type,
    subType: row.sub_type,
    executionResult: row.execution_result,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    executeAfter: row.execute_after,
    contextData: row.context_data,
    errorLog: row.error_log,
    attempts: row.attempts,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    contact: row.contact ? {
      id: row.contact.id,
      firstName: row.contact.first_name,
      lastName: row.contact.last_name,
      email: row.contact.email,
      phone: row.contact.phone,
    } : undefined,
  };
}

export function transformEventToCamelCase(row) {
  if (!row) return null;
  return {
    id: row.id,
    userId: row.user_id,
    eventType: row.event_type,
    payload: row.payload,
    createdAt: row.created_at,
  };
}

// ---------------------------------------------------------------------------
// Campaign Templates — CRUD
// ---------------------------------------------------------------------------

const TEMPLATE_SELECT = 'id, name, description, trigger_type, trigger_config, enrollment_rules, exit_conditions, category, is_playbook, created_by, ownership_type, is_visible, execution_scope, version, is_active, created_at, updated_at';
const TEMPLATE_WITH_NODES_SELECT = `${TEMPLATE_SELECT}, nodes:crm_template_nodes(id, template_id, node_type, sub_type, config, next_node_id, false_node_id, order_index, created_at, updated_at)`;

/**
 * Fetch campaign templates with optional ownership scoping.
 *
 * @param {object}  options
 * @param {boolean} options.activeOnly  - Only is_active templates (default true)
 * @param {string}  options.forUserId   - Return company templates + this user's
 *                                        personal templates (hides other users'
 *                                        personal campaigns)
 * @param {boolean} options.companyOnly - Return company templates only (admin
 *                                        Manage Templates table)
 */
export async function fetchTemplates({ activeOnly = true, forUserId = null, companyOnly = false } = {}) {
  const sb = getSupabase();
  let query = sb
    .from('crm_campaign_templates')
    .select(TEMPLATE_SELECT)
    .order('created_at', { ascending: false });

  if (activeOnly) {
    query = query.eq('is_active', true);
  }

  if (companyOnly) {
    // Admin Manage tab: includes Company Automations (execution_scope='company');
    // the page splits them into their own section.
    query = query.eq('ownership_type', 'company');
  } else if (forUserId) {
    // User-facing lists (Available Campaigns, enroll pickers, agent tools):
    // Company Automations are backend-only and never surfaced here.
    query = query
      .or(`ownership_type.eq.company,created_by.eq.${forUserId}`)
      .neq('execution_scope', 'company');
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function fetchTemplateById(templateId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_campaign_templates')
    .select(TEMPLATE_WITH_NODES_SELECT)
    .eq('id', templateId)
    .single();

  if (error) throw error;
  return data;
}

export async function createTemplate({ name, description, triggerType, triggerConfig, createdBy, ownershipType, executionScope, isVisible, enrollmentRules, exitConditions, category, isPlaybook }) {
  const sb = getSupabase();
  const row = {
    name,
    description: description || null,
    trigger_type: triggerType,
    trigger_config: triggerConfig || {},
    created_by: createdBy || null,
    ownership_type: ownershipType || 'company',
  };
  // Company Automations are created with execution_scope='company' and
  // is_visible=false; regular templates rely on the column defaults.
  if (executionScope !== undefined) row.execution_scope = executionScope;
  if (isVisible !== undefined) row.is_visible = isVisible;
  // Optional metadata (used by duplicateTemplate to carry the source over;
  // the Builder omits these and lets the column defaults apply).
  if (enrollmentRules !== undefined) row.enrollment_rules = enrollmentRules;
  if (exitConditions !== undefined) row.exit_conditions = exitConditions;
  if (category !== undefined) row.category = category;
  if (isPlaybook !== undefined) row.is_playbook = isPlaybook;

  const { data, error } = await sb
    .from('crm_campaign_templates')
    .insert(row)
    .select(TEMPLATE_SELECT)
    .single();

  if (error) throw error;
  return data;
}

export async function updateTemplate(templateId, updates) {
  const sb = getSupabase();
  const row = {};
  if (updates.name !== undefined) row.name = updates.name;
  if (updates.description !== undefined) row.description = updates.description;
  if (updates.triggerType !== undefined) row.trigger_type = updates.triggerType;
  if (updates.triggerConfig !== undefined) row.trigger_config = updates.triggerConfig;
  if (updates.isActive !== undefined) row.is_active = updates.isActive;
  if (updates.version !== undefined) row.version = updates.version;
  if (updates.enrollmentRules !== undefined) row.enrollment_rules = updates.enrollmentRules;
  if (updates.isVisible !== undefined) row.is_visible = updates.isVisible;
  if (updates.ownershipType !== undefined) row.ownership_type = updates.ownershipType;

  const { data, error } = await sb
    .from('crm_campaign_templates')
    .update(row)
    .eq('id', templateId)
    .select(TEMPLATE_SELECT)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete a template atomically via the delete_campaign_template RPC:
 * subscriptions + template go in one transaction (queue items cascade off the
 * user campaign rows), and the final state — template, nodes, subscriber
 * list, deleted_at/deleted_by — is preserved in crm_campaign_template_versions
 * so admins can restore it from "Recently Deleted" (restoreTemplateVersion).
 *
 * @param {string} templateId
 * @param {string|null} deletedBy - Firebase uid of the actor, recorded in the
 *                                  final snapshot (anon-key writes carry no
 *                                  user identity otherwise)
 * @returns {Promise<boolean>} false when the template was already gone
 */
export async function deleteTemplate(templateId, deletedBy = null) {
  const sb = getSupabase();
  const { data, error } = await sb.rpc('delete_campaign_template', {
    p_template_id: templateId,
    p_deleted_by: deletedBy,
  });

  if (error) throw error;
  return data;
}

/**
 * Duplicate a template into a brand-new, unlinked copy: fresh template row
 * (version 1, active) + cloned nodes with FRESH ids and all internal pointers
 * remapped (reusing source node ids would re-point the source campaign's rows
 * on the next save — see remapCampaignNodes). Nobody is auto-subscribed; the
 * copy appears in the owner's Available Campaigns / admin tables.
 *
 * @param {string} templateId - source template
 * @param {object} options
 * @param {string} options.name          - name for the copy (default "Copy of {source}")
 * @param {string} options.createdBy     - Firebase uid of the new owner
 * @param {string} options.ownershipType - 'user' (personal copy, default) | 'company'
 * @returns {Promise<object>} the new template row (snake_case)
 */
export async function duplicateTemplate(templateId, { name, createdBy, ownershipType = 'user' } = {}) {
  const source = await fetchTemplateById(templateId);

  const created = await createTemplate({
    name: name || `Copy of ${source.name}`,
    description: source.description,
    triggerType: source.trigger_type,
    triggerConfig: source.trigger_config,
    createdBy: createdBy || null,
    ownershipType,
    // A personal copy is always a normal, visible user-scope template — even
    // when the source is a hidden Company Automation. A company copy keeps
    // the source's scope/visibility (automation -> automation copy).
    executionScope: ownershipType === 'user' ? 'user' : (source.execution_scope || 'user'),
    isVisible: ownershipType === 'user' ? true : source.is_visible,
    enrollmentRules: source.enrollment_rules,
    exitConditions: source.exit_conditions,
    category: source.category,
    isPlaybook: source.is_playbook,
  });

  const remapped = remapCampaignNodes((source.nodes || []).map(transformNodeToCamelCase));
  await replaceTemplateNodes(created.id, remapped);

  return created;
}

// ---------------------------------------------------------------------------
// Template Versions — history, restore, recently deleted
// (crm_campaign_template_versions is written automatically by the
// trg_campaign_template_version_capture DB trigger on every template
// update/delete; see supabase/migrations/20261023_campaign_template_versioning.sql)
// ---------------------------------------------------------------------------

/**
 * Version history for a template, newest first. Each row's snapshot is
 * { template, nodes, subscribers?, deleted_at?, deleted_by? } — that
 * version's FINAL state.
 */
export async function fetchTemplateVersions(templateId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_campaign_template_versions')
    .select('id, template_id, version, created_by, created_at, snapshot')
    .eq('template_id', templateId)
    .order('version', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Deleted templates recoverable from history: latest snapshot per template_id
 * that no longer exists in crm_campaign_templates, newest deletion first.
 */
export async function fetchDeletedTemplateSnapshots() {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_deleted_campaign_snapshots')
    .select('*')
    .order('deleted_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Restore a template from its version history via the
 * restore_campaign_template RPC.
 *
 * - Template deleted  -> un-delete: full reinsert (template + nodes, original ids).
 * - Template exists   -> rollback: content fields + node set from the snapshot,
 *   version bumped to current + 1. Callers MUST then invoke the
 *   syncCampaignUpdates callable so subscriber clones pick up the change.
 *
 * @param {string} templateId
 * @param {number|null} version - specific version, or null for the latest snapshot
 * @returns {Promise<string>} the restored template id
 */
export async function restoreTemplateVersion(templateId, version = null) {
  const sb = getSupabase();
  const { data, error } = await sb.rpc('restore_campaign_template', {
    p_template_id: templateId,
    p_version: version,
  });

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Template Nodes — CRUD
// ---------------------------------------------------------------------------

export async function fetchNodesByTemplate(templateId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_template_nodes')
    .select('*')
    .eq('template_id', templateId)
    .order('order_index', { ascending: true });

  if (error) throw error;
  return data;
}

export async function upsertNode(node) {
  const sb = getSupabase();
  const row = {
    template_id: node.templateId,
    node_type: node.nodeType,
    sub_type: node.subType,
    config: node.config || {},
    next_node_id: node.nextNodeId || null,
    false_node_id: node.falseNodeId || null,
    order_index: node.orderIndex,
  };

  if (node.id) {
    row.id = node.id;
  }

  const { data, error } = await sb
    .from('crm_template_nodes')
    .upsert(row, { onConflict: 'id' })
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

/**
 * Bulk-replace all nodes for a template in one transaction-like operation.
 * Deletes existing nodes then inserts the new set. Each node object should
 * include a pre-generated `id` so that next_node_id / false_node_id pointers
 * are valid at insert time (UUIDs generated client-side via crypto.randomUUID
 * or the uuid package).
 */
export async function replaceTemplateNodes(templateId, nodes) {
  const sb = getSupabase();

  if (!nodes.length) {
    const { error: delError } = await sb
      .from('crm_template_nodes')
      .delete()
      .eq('template_id', templateId);
    if (delError) throw delError;
    return [];
  }

  const rows = nodes.map((n) => ({
    id: n.id,
    template_id: templateId,
    node_type: n.nodeType,
    sub_type: n.subType,
    config: n.config || {},
    next_node_id: n.nextNodeId || null,
    false_node_id: n.falseNodeId || null,
    order_index: n.orderIndex,
  }));

  const { data, error } = await sb
    .from('crm_template_nodes')
    .upsert(rows, { onConflict: 'id' })
    .select('*');

  if (error) throw error;

  const keepIds = nodes.map((n) => n.id);
  const { error: cleanupErr } = await sb
    .from('crm_template_nodes')
    .delete()
    .eq('template_id', templateId)
    .not('id', 'in', `(${keepIds.join(',')})`);

  if (cleanupErr) {
    console.warn('[replaceTemplateNodes] Cleanup of removed nodes failed:', cleanupErr.message);
  }

  return data;
}

export async function deleteNode(nodeId) {
  const sb = getSupabase();
  const { error } = await sb
    .from('crm_template_nodes')
    .delete()
    .eq('id', nodeId);

  if (error) throw error;
}

export async function reorderNodes(templateId, orderedIds) {
  const sb = getSupabase();
  const promises = orderedIds.map((id, index) =>
    sb
      .from('crm_template_nodes')
      .update({ order_index: index, next_node_id: orderedIds[index + 1] || null })
      .eq('id', id)
  );

  const results = await Promise.all(promises);
  const firstError = results.find((r) => r.error);
  if (firstError?.error) throw firstError.error;
}

// ---------------------------------------------------------------------------
// User Campaigns
// ---------------------------------------------------------------------------

const USER_CAMPAIGN_SELECT =
  'id, user_id, template_id, template_version, is_enabled, created_at, updated_at';
const USER_CAMPAIGN_WITH_TEMPLATE_SELECT =
  `${USER_CAMPAIGN_SELECT}, crm_campaign_templates(id, name, description, trigger_type, trigger_config, enrollment_rules, created_by, ownership_type, is_visible, execution_scope, version, is_active)`;

export async function fetchUserCampaigns(userId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_user_campaigns')
    .select(USER_CAMPAIGN_WITH_TEMPLATE_SELECT)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  // Company Automation "runner" subscriptions are managed from the admin
  // Company Automations table, never as a personal subscription — hide them
  // from My Campaigns, passive-campaign lists, and enroll pickers. Rows with
  // a deleted template (null join) pass through unchanged.
  return (data || []).filter(
    (row) => row.crm_campaign_templates?.execution_scope !== 'company'
  );
}

export async function fetchUserCampaignById(userCampaignId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_user_campaigns')
    .select(`${USER_CAMPAIGN_SELECT}, crm_user_campaign_nodes(*)`)
    .eq('id', userCampaignId)
    .single();

  if (error) throw error;
  return data;
}

export async function toggleUserCampaign(userCampaignId, isEnabled) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_user_campaigns')
    .update({ is_enabled: isEnabled })
    .eq('id', userCampaignId)
    .select(USER_CAMPAIGN_SELECT)
    .single();

  if (error) throw error;
  return data;
}

export async function deleteUserCampaign(userCampaignId) {
  const sb = getSupabase();
  const { error } = await sb
    .from('crm_user_campaigns')
    .delete()
    .eq('id', userCampaignId);

  if (error) throw error;
}

export async function fetchSubscribersByTemplate(templateId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_user_campaigns')
    .select(USER_CAMPAIGN_SELECT)
    .eq('template_id', templateId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Fetch the hidden "runner" subscriptions for Company Automation templates
 * (execution_scope='company'). Each automation has at most one runner —
 * normally owned by the admin who created it. Used by the admin Company
 * Automations table for the enable/disable toggle.
 */
export async function fetchAutomationRunners(templateIds) {
  if (!templateIds?.length) return [];
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_user_campaigns')
    .select(USER_CAMPAIGN_SELECT)
    .in('template_id', templateIds);

  if (error) throw error;
  return data || [];
}

export async function bulkToggleUserCampaigns(userCampaignIds, isEnabled) {
  if (!userCampaignIds?.length) return [];
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_user_campaigns')
    .update({ is_enabled: isEnabled })
    .in('id', userCampaignIds)
    .select(USER_CAMPAIGN_SELECT);

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Automation Queue — read-only from the frontend
// ---------------------------------------------------------------------------

export async function fetchQueueByContact(contactId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_automation_queue')
    .select('*')
    .eq('contact_id', contactId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function fetchQueueByUserCampaign(userCampaignId, { status } = {}) {
  const sb = getSupabase();
  let query = sb
    .from('crm_automation_queue')
    .select('*')
    .eq('user_campaign_id', userCampaignId)
    .order('created_at', { ascending: false });

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Contact Campaign Enrollments — enriched view for contact/deal detail panels
// ---------------------------------------------------------------------------

const ENROLLMENT_QUEUE_SELECT = `
  id, user_campaign_id, contact_id, current_node_id,
  status, node_type, sub_type, execution_result,
  execute_after, started_at, completed_at,
  error_log, attempts, context_data, created_at, updated_at,
  user_campaign:user_campaign_id (
    id, user_id, template_id, is_enabled,
    template:template_id (id, name, description, trigger_type, trigger_config, is_active, execution_scope)
  )
`;

/**
 * Fetch all campaign enrollments for a contact with full campaign metadata
 * and node definitions. Returns { queueItems, nodes } for the hook to merge.
 *
 * Company Automation enrollments (template execution_scope='company') are
 * backend-only and never shown as enrollments — they are filtered out here so
 * every consumer (enrollment cards, active-campaign badges, tab counts) hides
 * them consistently.
 */
export async function fetchContactCampaignEnrollments(contactId) {
  const sb = getSupabase();

  const { data: rawQueueItems, error: qErr } = await sb
    .from('crm_automation_queue')
    .select(ENROLLMENT_QUEUE_SELECT)
    .eq('contact_id', contactId)
    .order('created_at', { ascending: true });

  if (qErr) throw qErr;

  const queueItems = (rawQueueItems || []).filter(
    (q) => q.user_campaign?.template?.execution_scope !== 'company'
  );
  if (!queueItems.length) return { queueItems: [], nodes: [] };

  const uniqueCampaignIds = [...new Set(queueItems.map((q) => q.user_campaign_id))];

  const { data: nodes, error: nErr } = await sb
    .from('crm_user_campaign_nodes')
    .select('*')
    .in('user_campaign_id', uniqueCampaignIds)
    .order('order_index', { ascending: true });

  if (nErr) throw nErr;

  return { queueItems: queueItems || [], nodes: nodes || [] };
}

/**
 * Enroll a contact into a specific user campaign by inserting a queue item
 * at the campaign's first action node. Skips if the contact already has
 * pending/processing items in that campaign.
 */
export async function enrollContactInCampaign(userCampaignId, contactId, viewer = null) {
  const sb = getSupabase();

  // Visibility guard (Phase 4): the enrolling user must be able to see the
  // contact. `viewer` = { uid, isAdmin, accessibleOwnerIds? } — callers pass
  // it from their auth context; accessibleOwnerIds can be prefetched once for
  // bulk loops. Missing viewer info or missing contact row keeps the legacy
  // behavior (the queue processor is the final authority).
  if (viewer?.uid && !viewer.isAdmin) {
    const { data: contactRow } = await sb
      .from('crm_contacts')
      .select('id, owner_id, shared_with_ids, record_scope')
      .eq('id', contactId)
      .maybeSingle();
    if (contactRow) {
      const accessibleOwnerIds = Array.isArray(viewer.accessibleOwnerIds)
        ? viewer.accessibleOwnerIds
        : await fetchAccessibleOwnerIds(viewer.uid, 'contact');
      const visible = canUserSeeRecord({
        uid: viewer.uid,
        isAdmin: false,
        record: contactRow,
        accessibleOwnerIds,
      });
      if (!visible) {
        throw new Error("You don't have access to this contact");
      }
    }
  }

  // Check for existing active enrollment
  const { data: existing, error: exErr } = await sb
    .from('crm_automation_queue')
    .select('id')
    .eq('user_campaign_id', userCampaignId)
    .eq('contact_id', contactId)
    .in('status', ['pending', 'processing'])
    .limit(1);

  if (exErr) throw exErr;
  if (existing?.length) {
    throw new Error('Contact is already actively enrolled in this campaign');
  }

  // Fetch campaign nodes to find the first action node
  const { data: nodes, error: nErr } = await sb
    .from('crm_user_campaign_nodes')
    .select('*')
    .eq('user_campaign_id', userCampaignId)
    .order('order_index', { ascending: true });

  if (nErr) throw nErr;
  if (!nodes?.length) throw new Error('Campaign has no nodes');

  const actionNodes = nodes.filter((n) => n.node_type !== 'TRIGGER');
  if (!actionNodes.length) throw new Error('Campaign has no action nodes');

  // Walk the linked list to find the true first node
  const referencedAsNext = new Set();
  actionNodes.forEach((n) => {
    if (n.next_node_id) referencedAsNext.add(n.next_node_id);
    if (n.false_node_id) referencedAsNext.add(n.false_node_id);
  });
  const firstNode = actionNodes.find((n) => !referencedAsNext.has(n.id)) || actionNodes[0];

  const { data, error } = await sb
    .from('crm_automation_queue')
    .insert({
      user_campaign_id: userCampaignId,
      contact_id: contactId,
      current_node_id: firstNode.id,
      status: 'pending',
      execute_after: new Date().toISOString(),
      context_data: { source: 'manual_enrollment' },
    })
    .select('id')
    .single();

  if (error) throw error;
  return data;
}

/**
 * Unenroll a contact from a campaign by cancelling all pending/processing
 * queue items for that contact in the given user campaign.
 */
export async function unenrollContactFromCampaign(userCampaignId, contactId) {
  const sb = getSupabase();

  const { data: items, error: fErr } = await sb
    .from('crm_automation_queue')
    .select('id')
    .eq('user_campaign_id', userCampaignId)
    .eq('contact_id', contactId)
    .in('status', ['pending', 'processing']);

  if (fErr) throw fErr;
  if (!items?.length) return { cancelled: 0 };

  const ids = items.map((i) => i.id);
  const { error: uErr } = await sb
    .from('crm_automation_queue')
    .update({ status: 'cancelled', updated_at: new Date().toISOString() })
    .in('id', ids);

  if (uErr) throw uErr;
  return { cancelled: ids.length };
}

/**
 * Count a contact's active (pending/processing) queue items belonging to any
 * user campaign cloned from the given template. Enrollment is per-loan-officer,
 * so we resolve by template_id rather than a specific user_campaign_id.
 */
export async function getActiveEnrollmentCountByTemplate(contactId, templateId) {
  const sb = getSupabase();

  const { count, error } = await sb
    .from('crm_automation_queue')
    .select('id, user_campaign:user_campaign_id!inner(template_id)', { count: 'exact', head: true })
    .eq('contact_id', contactId)
    .eq('user_campaign.template_id', templateId)
    .in('status', ['pending', 'processing']);

  if (error) throw error;
  return count || 0;
}

/**
 * Unenroll a contact from every user campaign cloned from the given template by
 * cancelling all of their pending/processing queue items. Mirrors the backend
 * UNENROLL_CAMPAIGN handler but resolves by template_id from the frontend, so it
 * works regardless of which loan officer's clone the contact was enrolled in.
 */
export async function unenrollContactFromCampaignByTemplate(contactId, templateId) {
  const sb = getSupabase();

  const { data: items, error: fErr } = await sb
    .from('crm_automation_queue')
    .select('id, user_campaign:user_campaign_id!inner(template_id)')
    .eq('contact_id', contactId)
    .eq('user_campaign.template_id', templateId)
    .in('status', ['pending', 'processing']);

  if (fErr) throw fErr;
  if (!items?.length) return { cancelled: 0 };

  const ids = items.map((i) => i.id);
  const { error: uErr } = await sb
    .from('crm_automation_queue')
    .update({ status: 'cancelled', updated_at: new Date().toISOString() })
    .in('id', ids);

  if (uErr) throw uErr;
  return { cancelled: ids.length };
}

// ---------------------------------------------------------------------------
// Execution Logs — rich log queries with contact joins
// ---------------------------------------------------------------------------

const LOG_SELECT = `
  id, user_campaign_id, contact_id, current_node_id,
  status, node_type, sub_type, execution_result,
  execute_after, started_at, completed_at,
  error_log, attempts, context_data, created_at, updated_at,
  contact:contact_id ( id, first_name, last_name, email, phone )
`;

export async function fetchExecutionLogsByUserCampaign(userCampaignId) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_automation_queue')
    .select(LOG_SELECT)
    .eq('user_campaign_id', userCampaignId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function fetchExecutionLogsByTemplate(templateId) {
  const sb = getSupabase();
  const { data: campaigns, error: campErr } = await sb
    .from('crm_user_campaigns')
    .select('id')
    .eq('template_id', templateId);

  if (campErr) throw campErr;
  if (!campaigns?.length) return [];

  const campaignIds = campaigns.map((c) => c.id);
  const { data, error } = await sb
    .from('crm_automation_queue')
    .select(LOG_SELECT)
    .in('user_campaign_id', campaignIds)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Automation Events — read-only from the frontend
// ---------------------------------------------------------------------------

export async function fetchEventsByUser(userId, { limit = 50 } = {}) {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_automation_events')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Template Stats (admin view)
// ---------------------------------------------------------------------------

export async function fetchTemplateStats() {
  const sb = getSupabase();
  const { data, error } = await sb
    .from('crm_campaign_template_stats')
    .select('*');

  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Realtime subscriptions
// ---------------------------------------------------------------------------

/**
 * Subscribe to changes on the user's campaigns (enable/disable toggling,
 * new subscriptions, deletions). Returns the channel for cleanup.
 */
export function subscribeToUserCampaigns(userId, onChange) {
  const sb = getSupabase();
  const suffix = Math.random().toString(36).slice(2, 8);
  const channel = sb
    .channel(`user-campaigns-${userId}-${suffix}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'crm_user_campaigns',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => onChange(payload)
    )
    .subscribe();

  return channel;
}

/**
 * Subscribe to automation queue changes for a specific contact (for a
 * contact-detail timeline view showing automation progress).
 */
export function subscribeToQueueByContact(contactId, onChange) {
  const sb = getSupabase();
  const suffix = Math.random().toString(36).slice(2, 8);
  const channel = sb
    .channel(`automation-queue-contact-${contactId}-${suffix}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'crm_automation_queue',
        filter: `contact_id=eq.${contactId}`,
      },
      (payload) => onChange(payload)
    )
    .subscribe();

  return channel;
}
